import type { Filter, SimpleFilter } from "../FindParameters";
import type { AtomData } from "../VersionAtom";

/**
 * Client-side counterpart of the `contains` / `starts` / `ends` find filters.
 *
 * The find procedures compare text in a "search text" form: Unicode NFC (a
 * decomposed "o" + U+0308 equals the composed "ö"), every whitespace run
 * collapsed to one space, trimmed. These helpers apply the same rules, so a
 * filter in the browser finds what a server-side find finds.
 */

/**
 * Text operators of the find filters (`StringParameter`), plus `allWords`:
 * every word of the term is contained in the text, in any order and with any
 * gap, as the filter of {@link buildSearchTextFilter} matches a single field.
 */
export type SearchTextMode = "contains" | "starts" | "ends" | "allWords";

/**
 * The search-text form of `value`: NFC, whitespace runs as one space, trimmed.
 * Case is kept, so it also serves to normalise single-line labels on write.
 */
export function normalizeSearchText(value: string): string {
  return value.normalize("NFC").replaceAll(/\s+/g, " ").trim();
}

/**
 * Whether `text` matches the search `term` the way the find filter of the
 * same name does: both in the search-text form, case-insensitive, `%` and `_`
 * literal. An empty term matches every text; a missing text matches nothing.
 *
 * Case is folded one character at a time: "İ" becomes "i" and a final "Σ"
 * becomes "σ", not the contextual "ς". With the ICU search collation of the
 * generated finds (atoms2#126), the server folds case whatever the database
 * ctype, but lowers a whole string at once, so terms with these two letters
 * can match differently on client and server. Without it, a database with
 * ctype `C` folds only ASCII letters.
 */
export function matchesSearchText(
  text: string | null | undefined,
  term: string,
  mode: SearchTextMode = "contains",
): boolean {
  if (text === null || text === undefined) return false;
  const haystack = foldCase(normalizeSearchText(text));
  const needle = foldCase(normalizeSearchText(term));
  switch (mode) {
    case "allWords":
      return searchTextWords(term).every((word) =>
        haystack.includes(foldCase(word)),
      );
    case "starts":
      return haystack.startsWith(needle);
    case "ends":
      return haystack.endsWith(needle);
    default:
      return haystack.includes(needle);
  }
}

/**
 * The words of a search `term`: its search-text form split at the spaces,
 * without exact repeats. Words that differ only in case are kept, as the
 * server may fold their case differently than the client. Punctuation stays part of its word
 * ("§21," is one word). An empty or whitespace-only term has no words.
 */
export function searchTextWords(term: string): string[] {
  const words = normalizeSearchText(term).split(" ");
  return [...new Set(words)].filter((word) => word !== "");
}

/**
 * The find filter of a global search: a row matches when every word of
 * `term` is contained in at least one of `fields`, so word order and gaps
 * don't matter ("Broschüre AVZ" finds "AVZ-Broschüre §21, A"). It is an AND
 * over the words of an OR over the fields of `contains`; one word gives the
 * OR alone. Each `contains` uses the trigram index of its field, but a
 * short word such as "A" yields no trigram and is matched without the index.
 *
 * The result is a superset of a `contains` of the whole term. Returns
 * `undefined` when the term has no words or no field is given.
 */
export function buildSearchTextFilter<T extends AtomData = AtomData>(
  term: string | null | undefined,
  fields: ReadonlyArray<keyof T & string>,
): Filter<T> | undefined {
  const words = searchTextWords(term ?? "");
  if (words.length === 0 || fields.length === 0) return undefined;
  const clauses: Array<Filter<T>> = words.map((word) => ({
    OR: fields.map(
      (field) => ({ [field]: { contains: word } }) as SimpleFilter<T>,
    ),
  }));
  return clauses.length === 1 ? clauses[0] : { AND: clauses };
}

/**
 * Lowercase each code point on its own and keep one code point per character,
 * as Postgres does with a libc locale: `toLowerCase` on the whole string would
 * pick a contextual final sigma and expand "İ" to two code points.
 */
function foldCase(value: string): string {
  return Array.from(value, (char) => {
    const lower = char.toLowerCase().codePointAt(0);
    return lower === undefined ? char : String.fromCodePoint(lower);
  }).join("");
}
